import React, { useMemo, useState } from "react";
import { useTranslation } from "../i18n";

import { useNavigate } from "react-router-dom";

/* =========================================================
   DOCUMENT STATS
========================================================= */

const stats = [
  {
    title: "Total documents",
    value: "846",
    description: "14.2% vs last month",
    growth: "up",
    colorClass: "orange",
    icon: "/assets/totalrevenue.svg",
  },
  {
    title: "Pending review",
    value: "46",
    description: "8 require priority review",
    growth: "up",
    colorClass: "green",
    icon: "/assets/totalrevenue.svg",
  },
  {
    title: "Verified",
    value: "782",
    description: "92.4% verification rate",
    growth: "up",
    colorClass: "blue",
    icon: "/assets/totalrevenue.svg",
  },
  {
    title: "Expiring soon",
    value: "18",
    description: "Within next 30 days",
    growth: "down",
    colorClass: "red",
    icon: "/assets/totalrevenue.svg",
  },
];

/* =========================================================
   DOCUMENT DATA
========================================================= */

const documentRows = [
  {
    id: 1,
    documentId: "DOC-2024-0846",
    documentName: "Certificate of Origin",
    fileName: "certificate-origin.pdf",
    operation: "OP-2024-0456",
    type: "Trade Document",
    uploadedBy: "Zambezi Mining Ltd.",
    uploadedDate: "May 28, 2024",
    expiryDate: "May 28, 2025",
    status: "Verified",
    statusClass: "verified",
  },
  {
    id: 2,
    documentId: "DOC-2024-0845",
    documentName: "Commercial Invoice",
    fileName: "commercial-invoice.pdf",
    operation: "OP-2024-0455",
    type: "Invoice",
    uploadedBy: "Metalex Trading FZE",
    uploadedDate: "May 27, 2024",
    expiryDate: "—",
    status: "Pending Review",
    statusClass: "pending-review",
  },
  {
    id: 3,
    documentId: "DOC-2024-0844",
    documentName: "Bill of Lading",
    fileName: "bill-of-lading.pdf",
    operation: "OP-2024-0454",
    type: "Shipping Document",
    uploadedBy: "Energy Sphere Ltd.",
    uploadedDate: "May 26, 2024",
    expiryDate: "—",
    status: "Verified",
    statusClass: "verified",
  },
  {
    id: 4,
    documentId: "DOC-2024-0843",
    documentName: "Quality Certificate",
    fileName: "quality-certificate.pdf",
    operation: "OP-2024-0453",
    type: "Compliance",
    uploadedBy: "CoalFields Africa",
    uploadedDate: "May 25, 2024",
    expiryDate: "Jun 18, 2024",
    status: "Expiring Soon",
    statusClass: "expiring-soon",
  },
  {
    id: 5,
    documentId: "DOC-2024-0842",
    documentName: "Insurance Certificate",
    fileName: "insurance-certificate.pdf",
    operation: "OP-2024-0452",
    type: "Insurance",
    uploadedBy: "PetroSource Co.",
    uploadedDate: "May 24, 2024",
    expiryDate: "Dec 24, 2024",
    status: "Verified",
    statusClass: "verified",
  },
  {
    id: 6,
    documentId: "DOC-2024-0841",
    documentName: "Customs Declaration",
    fileName: "customs-declaration.pdf",
    operation: "OP-2024-0451",
    type: "Customs",
    uploadedBy: "Global Commodities Ltd.",
    uploadedDate: "May 23, 2024",
    expiryDate: "—",
    status: "Rejected",
    statusClass: "rejected",
  },
];

export default function Documents() {
  const { t, text, status: translateStatus } = useTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("30");

  /* =========================================================
     FILTER DOCUMENTS
  ========================================================= */

  const filteredDocuments = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return documentRows.filter((item) => {
      const matchesSearch =
        !searchValue ||
        item.documentId.toLowerCase().includes(searchValue) ||
        item.documentName.toLowerCase().includes(searchValue) ||
        item.fileName.toLowerCase().includes(searchValue) ||
        item.operation.toLowerCase().includes(searchValue) ||
        item.type.toLowerCase().includes(searchValue) ||
        item.uploadedBy.toLowerCase().includes(searchValue) ||
        item.status.toLowerCase().includes(searchValue);

      const matchesStatus =
        !statusFilter || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [search, statusFilter]);

  return (
    <div className="dashboard-page inquiries-page">
      <div className="inquiries-page-header">
        <div>
          <h1 className="f-s-28 l-h-32 fw-semibold">{t("ui.documents")}</h1>
          <p className="f-s-14 l-h-20">{t("ui.manage.review.and.verify.trade.documents.across.all.operations")}</p>
        </div>
      </div>
      <div className="operation-stats-grid">
        {stats.map((stat, index) => (
          <div
            className="operation-stat-card"
            key={index}
          >
            <div
              className={`operation-stat-icon ${stat.colorClass}`}
            >
              <img
                src={stat.icon}
                alt=""
              />
            </div>

            <div className="operation-stat-content">
              <span className="operation-stat-title">
                {text(stat.title)}
              </span>

              <strong className="operation-stat-value">
                {stat.value}
              </strong>

              <span
                className={`operation-stat-growth ${
                  stat.growth === "up"
                    ? "positive"
                    : "negative"
                }`}
              >
                <span className="growth-arrow">
                  {stat.growth === "up" ? "↗" : "↘"}
                </span>

                {text(stat.description)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* =====================================================
          DOCUMENT TABLE
      ===================================================== */}

      <div className="inquiries-table-card">
        {/* TABLE HEADER */}

        <div className="inquiries-table-header">
          <div>
            <h2>{t("ui.document.library")}</h2>

            <p>{t("ui.view.and.manage.recently.uploaded.documents")}</p>
          </div>

          <div className="inquiries-table-actions">
            {/* SEARCH */}

            <div className="inquiries-search">
              <img
                src="/assets/search-icon.svg"
                alt=""
              />

              <input
                type="text"
                placeholder={t("ui.search.documents")}
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                >
                  ×
                </button>
              )}
            </div>

            {/* STATUS FILTER */}

            <div className="inquiries-status-filter">
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value)
                }
              >
                <option value="">{t("ui.all.status.eg6xm9")}</option>

                <option value="Verified">{t("ui.verified")}</option>

                <option value="Pending Review">{t("ui.pending.review")}</option>

                <option value="Expiring Soon">{t("ui.expiring.soon")}</option>

                <option value="Rejected">{t("ui.rejected")}</option>
              </select>
            </div>
          </div>
        </div>

        {/* =====================================================
            TABLE
        ===================================================== */}

        <div className="inquiries-table-wrapper">
          <table className="inquiries-modern-table operations-modern-table">
            <thead>
              <tr>
                <th>{t("ui.document")}</th>

                <th>{t("ui.operation")}</th>

                <th>{t("ui.type")}</th>

                <th>{t("ui.uploaded.by")}</th>

                <th>{t("ui.uploaded.date")}</th>

                <th>{t("ui.expiry.date")}</th>

                <th>{t("ui.status")}</th>

                <th className="inquiry-actions-heading">{t("ui.actions")}</th>
              </tr>
            </thead>

            <tbody>
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map((item) => (
                  <tr key={item.id}>
                    {/* =====================================
                        DOCUMENT
                    ===================================== */}

                    <td>
                      <div className="document-name-cell">
                        <div className="document-file-icon">
                          <img
                            src="/assets/document.svg"
                            alt=""
                          />
                        </div>

                        <div className="document-name-info">
                          <strong>
                            {item.documentName}
                          </strong>

                          <span>
                            {item.documentId}
                          </span>

                          <small>
                            {item.fileName}
                          </small>
                        </div>
                      </div>
                    </td>

                    {/* =====================================
                        OPERATION
                    ===================================== */}

                    <td>
                      <span className="transaction-operation-id">
                        {item.operation}
                      </span>
                    </td>

                    {/* =====================================
                        TYPE
                    ===================================== */}

                    <td>
                      <div className="transaction-task-cell">
                        {item.type}
                      </div>
                    </td>

                    {/* =====================================
                        UPLOADED BY
                    ===================================== */}

                    <td>
                      <div className="document-uploader">
                        <strong>
                          {item.uploadedBy}
                        </strong>
                      </div>
                    </td>

                    {/* =====================================
                        UPLOADED DATE
                    ===================================== */}

                    <td>
                      <span className="transaction-date">
                        {item.uploadedDate}
                      </span>
                    </td>

                    {/* =====================================
                        EXPIRY DATE
                    ===================================== */}

                    <td>
                      <span
                        className={`document-expiry ${
                          item.statusClass === "expiring-soon"
                            ? "warning"
                            : ""
                        }`}
                      >
                        {item.expiryDate}
                      </span>
                    </td>

                    {/* =====================================
                        STATUS
                    ===================================== */}

                    <td>
                      <div className="operation-stage-cell">
                        <span
                          className={`operation-stage-status ${item.statusClass}`}
                        >
                          {translateStatus(item.status)}
                        </span>
                      </div>
                    </td>

                    {/* =====================================
                        ACTION
                    ===================================== */}

                    <td>
                      <div className="operation-table-actions">
                        <button
                          type="button"
                          className="operation-view-btn"
                          title={t("ui.view.document")}
                          onClick={() =>
                            navigate(
                              `/documents/${item.id}`
                            )
                          }
                        >
                          <img
                            src="/assets/eye.svg"
                            alt={t("ui.view")}
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8">
                    <div className="inquiries-empty-state">
                      <img
                        src="/assets/search-icon.svg"
                        alt=""
                      />

                      <strong>{t("ui.no.documents.found")}</strong>

                      <span>{t("ui.try.changing.your.search.or.filters.16umm1j")}</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="inquiries-table-footer">
          <span>{t("ui.showing.1onf62t")}{" "}
            {filteredDocuments.length > 0 ? 1 : 0}
            {" to "}
            {filteredDocuments.length}
            {" of "}

            <strong>
              {documentRows.length}
            </strong>

            {" documents"}
          </span>

          <div className="inquiries-pagination">
            <button type="button">
              ‹
            </button>

            <button
              type="button"
              className="active"
            >
              1
            </button>

            <button type="button">
              2
            </button>

            <button type="button">
              3
            </button>

            <button type="button">
              4
            </button>

            <button type="button">
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
